/* Category-level landing pages — "explained simply" overviews for main nav categories. */

export const categoryPages = {
  'restaurant-technology': {
    slug: 'restaurant-technology',
    icon: 'Cpu',
    en: {
      eyebrow: 'RESTAURANT TECHNOLOGY',
      title: 'All Your Restaurant Tech,',
      titleAccent: 'Finally Working Together.',
      intro:
        'POS, payments, online ordering, delivery tablets, printers, Wi-Fi… If it plugs in or connects, we set it up, connect it and keep it running — so you never lose a sale to a frozen screen again.',
      simpleHeading: 'What is this, in plain words?',
      simpleAnalogy:
        'Think of us as the IT department big restaurant chains have — but on demand, for your restaurant. One phone number, one team, every technical problem solved.',
      simplePoints: [
        'Your POS (the machine where you ring up orders) always updated and working.',
        'Online orders from Uber Eats, DoorDash and your website flowing into one place.',
        'No more calling 5 different vendors when something breaks.',
        'Real humans answering, 24/7, in English and Spanish.',
      ],
      servicesHeading: 'What lives inside this category',
      servicesIntro: 'Four services. One connected technology stack. Click any of them to go deeper.',
      services: [
        {
          icon: 'MonitorSmartphone',
          name: 'IT & POS Systems',
          what: 'We deploy, manage and support your POS, hardware, network and payments.',
          plain: 'In plain words: your personal tech department, without the payroll.',
          href: '/it-pos-systems',
        },
        {
          icon: 'Settings',
          name: 'POS Management',
          what: 'Menu updates, price changes, modifiers and 86’d items handled for you.',
          plain: 'In plain words: you text us the change, it appears everywhere.',
          href: '/services/pos-management',
        },
        {
          icon: 'Plug',
          name: 'POS Integrations',
          what: 'We connect your POS with delivery apps, accounting, loyalty and more.',
          plain: 'In plain words: all your systems finally talk to each other.',
          href: '/services/pos-integrations',
        },
        {
          icon: 'ShoppingBag',
          name: 'Online Ordering',
          what: 'Commission-free direct ordering plus optimized delivery channels.',
          plain: 'In plain words: more orders that go straight into your kitchen.',
          href: '/services/online-ordering',
        },
      ],
      forYouHeading: 'This is for you if…',
      forYou: [
        'Your POS crashes on a Friday night and nobody picks up the phone.',
        'You re-type delivery orders by hand into your POS.',
        'You pay 4 different tech vendors and none of them talk to each other.',
        'Updating a price means touching 5 different systems.',
      ],
      ctaTitle: 'Not sure where to start? Start free.',
      ctaText:
        'Our free audit shows you exactly which systems are leaking money and how to fix them — no commitment, no sales pressure.',
    },
    es: {
      eyebrow: 'TECNOLOGÍA PARA RESTAURANTES',
      title: 'Toda la Tecnología de tu Restaurante,',
      titleAccent: 'Por Fin Funcionando Junta.',
      intro:
        'POS, pagos, pedidos online, tabletas de delivery, impresoras, Wi-Fi… Si se enchufa o se conecta, nosotros lo instalamos, lo conectamos y lo mantenemos funcionando — para que nunca más pierdas una venta por una pantalla congelada.',
      simpleHeading: '¿Qué es esto, en palabras simples?',
      simpleAnalogy:
        'Piensa en nosotros como el departamento de sistemas que tienen las grandes cadenas — pero a tu medida, para tu restaurante. Un solo teléfono, un solo equipo, todos los problemas técnicos resueltos.',
      simplePoints: [
        'Tu POS (la máquina donde cobras) siempre actualizado y funcionando.',
        'Los pedidos de Uber Eats, DoorDash y tu web llegando a un solo lugar.',
        'Se acabó llamar a 5 proveedores distintos cuando algo falla.',
        'Personas reales respondiendo 24/7, en español e inglés.',
      ],
      servicesHeading: 'Qué incluye esta categoría',
      servicesIntro: 'Cuatro servicios. Un solo ecosistema tecnológico conectado. Haz clic en cualquiera para profundizar.',
      services: [
        {
          icon: 'MonitorSmartphone',
          name: 'IT y Sistemas POS',
          what: 'Instalamos, gestionamos y damos soporte a tu POS, hardware, red y pagos.',
          plain: 'En palabras simples: tu departamento técnico personal, sin nómina.',
          href: '/it-pos-systems',
        },
        {
          icon: 'Settings',
          name: 'Gestión de POS',
          what: 'Cambios de menú, precios, modificadores y productos agotados, gestionados por ti.',
          plain: 'En palabras simples: nos escribes el cambio y aparece en todos lados.',
          href: '/services/pos-management',
        },
        {
          icon: 'Plug',
          name: 'Integraciones de POS',
          what: 'Conectamos tu POS con apps de delivery, contabilidad, lealtad y más.',
          plain: 'En palabras simples: todos tus sistemas por fin se hablan entre sí.',
          href: '/services/pos-integrations',
        },
        {
          icon: 'ShoppingBag',
          name: 'Pedidos Online',
          what: 'Pedidos directos sin comisiones más canales de delivery optimizados.',
          plain: 'En palabras simples: más pedidos que llegan directo a tu cocina.',
          href: '/services/online-ordering',
        },
      ],
      forYouHeading: 'Esto es para ti si…',
      forYou: [
        'Tu POS se congela un viernes por la noche y nadie te contesta el teléfono.',
        'Pasas pedidos de delivery a mano a tu POS.',
        'Pagas a 4 proveedores tecnológicos y ninguno se comunica con el otro.',
        'Cambiar un precio significa tocar 5 sistemas distintos.',
      ],
      ctaTitle: '¿No sabes por dónde empezar? Empieza gratis.',
      ctaText:
        'Nuestra auditoría gratuita te muestra exactamente qué sistemas están perdiendo dinero y cómo arreglarlos — sin compromiso, sin presión de venta.',
    },
  },

  'digital-presence': {
    slug: 'digital-presence',
    icon: 'Globe',
    en: {
      eyebrow: 'DIGITAL PRESENCE',
      title: 'Be Found First.',
      titleAccent: 'Be Chosen More.',
      intro:
        'When a hungry customer searches "tacos near me", 92% pick from the first results they see. We make sure that result is you — on Google, Maps, your website and every review platform.',
      simpleHeading: 'What is this, in plain words?',
      simpleAnalogy:
        'Your digital presence is your restaurant’s storefront on the internet. If it looks empty, outdated or hard to find, customers walk past — straight to your competitor.',
      simplePoints: [
        'Show up at the top when people search for food in your area.',
        'A website that actually takes orders and reservations, not just photos.',
        'A Google profile with mouth-watering photos and answered reviews.',
        'A brand people recognize and remember.',
      ],
      servicesHeading: 'What lives inside this category',
      servicesIntro: 'Five services that turn searches into seated (and ordering) customers.',
      services: [
        {
          icon: 'Sparkles',
          name: 'Digital Branding',
          what: 'Logo, colors, voice and visuals that make your restaurant memorable.',
          plain: 'In plain words: the reason customers remember you and not the guy next door.',
          href: '/digital-branding',
        },
        {
          icon: 'Globe',
          name: 'Restaurant Websites',
          what: 'Fast, beautiful websites built to convert visits into orders.',
          plain: 'In plain words: a website that sells, not just a digital brochure.',
          href: '/services/restaurant-websites',
        },
        {
          icon: 'Search',
          name: 'Local SEO',
          what: 'We optimize your presence so you rank when locals search for food.',
          plain: 'In plain words: appearing first when someone nearby is hungry.',
          href: '/services/local-seo',
        },
        {
          icon: 'MapPin',
          name: 'Google Business Profile',
          what: 'Full management of your Google and Maps profile — photos, posts, hours, menus.',
          plain: 'In plain words: your most important free advertising, done right.',
          href: '/services/google-business-profile',
        },
        {
          icon: 'Star',
          name: 'Online Reputation',
          what: 'Review monitoring, professional responses and rating growth strategies.',
          plain: 'In plain words: turning stars into customers, and complaints into comebacks.',
          href: '/services/online-reputation',
        },
      ],
      forYouHeading: 'This is for you if…',
      forYou: [
        'Your competitor shows up on Google Maps before you do.',
        'Your website looks great but generates zero orders.',
        'You have unanswered reviews from 8 months ago.',
        'Customers say "I didn’t know you were here!"',
      ],
      ctaTitle: 'Find out how visible you really are — free.',
      ctaText:
        'Our free audit scans your Google presence, website and reviews, and shows you exactly what customers see when they search for you.',
    },
    es: {
      eyebrow: 'PRESENCIA DIGITAL',
      title: 'Que Te Encuentren Primero.',
      titleAccent: 'Que Te Elijan Más.',
      intro:
        'Cuando un cliente con hambre busca "tacos cerca de mí", el 92% elige entre los primeros resultados que ve. Nosotros hacemos que ese resultado seas tú — en Google, Maps, tu web y todas las plataformas de reseñas.',
      simpleHeading: '¿Qué es esto, en palabras simples?',
      simpleAnalogy:
        'Tu presencia digital es la fachada de tu restaurante en internet. Si se ve vacía, desactualizada o difícil de encontrar, los clientes pasan de largo — directo a tu competencia.',
      simplePoints: [
        'Aparecer arriba cuando la gente busca comida en tu zona.',
        'Una web que realmente toma pedidos y reservas, no solo fotos.',
        'Un perfil de Google con fotos que abren el apetito y reseñas respondidas.',
        'Una marca que la gente reconoce y recuerda.',
      ],
      servicesHeading: 'Qué incluye esta categoría',
      servicesIntro: 'Cinco servicios que convierten búsquedas en clientes sentados (y pidiendo).',
      services: [
        {
          icon: 'Sparkles',
          name: 'Branding Digital',
          what: 'Logo, colores, voz y visuales que hacen tu restaurante memorable.',
          plain: 'En palabras simples: la razón por la que te recuerdan a ti y no al de al lado.',
          href: '/digital-branding',
        },
        {
          icon: 'Globe',
          name: 'Sitios Web para Restaurantes',
          what: 'Webs rápidas y hermosas, construidas para convertir visitas en pedidos.',
          plain: 'En palabras simples: una web que vende, no solo un folleto digital.',
          href: '/services/restaurant-websites',
        },
        {
          icon: 'Search',
          name: 'SEO Local',
          what: 'Optimizamos tu presencia para que aparezcas cuando buscan comida cerca.',
          plain: 'En palabras simples: salir primero cuando alguien cerca tiene hambre.',
          href: '/services/local-seo',
        },
        {
          icon: 'MapPin',
          name: 'Perfil de Google Business',
          what: 'Gestión completa de tu perfil en Google y Maps — fotos, posts, horarios, menús.',
          plain: 'En palabras simples: tu publicidad gratuita más importante, bien hecha.',
          href: '/services/google-business-profile',
        },
        {
          icon: 'Star',
          name: 'Reputación Online',
          what: 'Monitoreo de reseñas, respuestas profesionales y estrategias para subir tu calificación.',
          plain: 'En palabras simples: convertir estrellas en clientes, y quejas en oportunidades.',
          href: '/services/online-reputation',
        },
      ],
      forYouHeading: 'Esto es para ti si…',
      forYou: [
        'Tu competencia aparece en Google Maps antes que tú.',
        'Tu web se ve bien pero genera cero pedidos.',
        'Tienes reseñas sin responder desde hace 8 meses.',
        'Los clientes te dicen "¡no sabía que estaban aquí!"',
      ],
      ctaTitle: 'Descubre qué tan visible eres realmente — gratis.',
      ctaText:
        'Nuestra auditoría gratuita escanea tu presencia en Google, tu web y tus reseñas, y te muestra exactamente lo que ven tus clientes cuando te buscan.',
    },
  },

  'restaurant-growth': {
    slug: 'restaurant-growth',
    icon: 'TrendingUp',
    en: {
      eyebrow: 'RESTAURANT GROWTH',
      title: 'More Orders. Bigger Tickets.',
      titleAccent: 'Customers Who Come Back.',
      intro:
        'Growth isn’t luck — it’s a system. We combine delivery optimization, catering expansion, smart marketing and pricing strategy to grow your revenue measurably in 60–90 days.',
      simpleHeading: 'What is this, in plain words?',
      simpleAnalogy:
        'Imagine having a growth team like the big chains have — analysts, marketers, delivery experts — working on YOUR restaurant every week. That’s this category.',
      simplePoints: [
        'Sell more on Uber Eats, DoorDash and Grubhub without losing margin.',
        'Win corporate catering orders worth $500–$5,000 each.',
        'Bring first-time customers back automatically with smart marketing.',
        'Know exactly which dishes make you money — and which don’t.',
      ],
      servicesHeading: 'What lives inside this category',
      servicesIntro: 'Four growth engines that compound on each other. Click any of them to go deeper.',
      services: [
        {
          icon: 'TrendingUp',
          name: 'Revenue Growth',
          what: 'Pricing strategy, menu engineering and channel optimization for profitability.',
          plain: 'In plain words: a plan to make more money with what you already have.',
          href: '/restaurant-revenue-growth',
        },
        {
          icon: 'Bike',
          name: 'Delivery Optimization',
          what: 'We optimize your Uber Eats, DoorDash and Grubhub listings, menus and promos.',
          plain: 'In plain words: ranking higher and selling more inside the delivery apps.',
          href: '/services/delivery-optimization',
        },
        {
          icon: 'Utensils',
          name: 'Catering Growth',
          what: 'ezCater setup, corporate outreach and large-order operations.',
          plain: 'In plain words: one catering order can equal 50 regular tickets.',
          href: '/services/catering-growth',
        },
        {
          icon: 'Mail',
          name: 'Customer Marketing',
          what: 'Email, SMS and loyalty automations that bring customers back.',
          plain: 'In plain words: turning one-time visitors into weekly regulars.',
          href: '/services/customer-marketing',
        },
      ],
      forYouHeading: 'This is for you if…',
      forYou: [
        'Sales are flat and you don’t know which lever to pull.',
        'Delivery apps eat 30% of your margin and you feel trapped.',
        'You have hundreds of customer contacts and never message them.',
        'Catering sounds great but you don’t know where to start.',
      ],
      ctaTitle: 'Get your free growth plan.',
      ctaText:
        'Our free audit identifies your 3 biggest revenue opportunities — most owners are surprised by what they find.',
    },
    es: {
      eyebrow: 'CRECIMIENTO DEL RESTAURANTE',
      title: 'Más Pedidos. Tickets Más Grandes.',
      titleAccent: 'Clientes Que Regresan.',
      intro:
        'Crecer no es suerte — es un sistema. Combinamos optimización de delivery, expansión de catering, marketing inteligente y estrategia de precios para hacer crecer tus ingresos de forma medible en 60–90 días.',
      simpleHeading: '¿Qué es esto, en palabras simples?',
      simpleAnalogy:
        'Imagina tener un equipo de crecimiento como el de las grandes cadenas — analistas, marketers, expertos en delivery — trabajando en TU restaurante cada semana. Eso es esta categoría.',
      simplePoints: [
        'Vender más en Uber Eats, DoorDash y Grubhub sin perder margen.',
        'Ganar pedidos de catering corporativo de $500–$5,000 cada uno.',
        'Hacer que los clientes nuevos regresen automáticamente con marketing inteligente.',
        'Saber exactamente qué platillos te dan dinero — y cuáles no.',
      ],
      servicesHeading: 'Qué incluye esta categoría',
      servicesIntro: 'Cuatro motores de crecimiento que se potencian entre sí. Haz clic en cualquiera para profundizar.',
      services: [
        {
          icon: 'TrendingUp',
          name: 'Crecimiento de Ingresos',
          what: 'Estrategia de precios, ingeniería de menú y optimización de canales para rentabilidad.',
          plain: 'En palabras simples: un plan para ganar más dinero con lo que ya tienes.',
          href: '/restaurant-revenue-growth',
        },
        {
          icon: 'Bike',
          name: 'Optimización de Delivery',
          what: 'Optimizamos tus perfiles, menús y promos en Uber Eats, DoorDash y Grubhub.',
          plain: 'En palabras simples: aparecer más arriba y vender más dentro de las apps.',
          href: '/services/delivery-optimization',
        },
        {
          icon: 'Utensils',
          name: 'Crecimiento de Catering',
          what: 'Configuración de ezCater, alcance corporativo y operación de pedidos grandes.',
          plain: 'En palabras simples: un pedido de catering puede valer 50 tickets normales.',
          href: '/services/catering-growth',
        },
        {
          icon: 'Mail',
          name: 'Marketing al Cliente',
          what: 'Automatizaciones de email, SMS y lealtad que hacen regresar a tus clientes.',
          plain: 'En palabras simples: convertir visitantes de una vez en clientes de cada semana.',
          href: '/services/customer-marketing',
        },
      ],
      forYouHeading: 'Esto es para ti si…',
      forYou: [
        'Las ventas están estancadas y no sabes qué palanca mover.',
        'Las apps de delivery se comen el 30% de tu margen y te sientes atrapado.',
        'Tienes cientos de contactos de clientes y nunca les escribes.',
        'El catering suena bien pero no sabes por dónde empezar.',
      ],
      ctaTitle: 'Recibe tu plan de crecimiento gratis.',
      ctaText:
        'Nuestra auditoría gratuita identifica tus 3 mayores oportunidades de ingresos — la mayoría de los dueños se sorprende con lo que encuentra.',
    },
  },

  'restaurant-intelligence': {
    slug: 'restaurant-intelligence',
    icon: 'PieChart',
    en: {
      eyebrow: 'RESTAURANT INTELLIGENCE',
      title: 'Stop Guessing.',
      titleAccent: 'Start Knowing.',
      intro:
        'Your restaurant produces thousands of data points every day — sales, labor, delivery, reviews. We turn that noise into clear answers: what to sell, when to staff, where money leaks.',
      simpleHeading: 'What is this, in plain words?',
      simpleAnalogy:
        'It’s like having an accountant, an analyst and a consultant reviewing your numbers every single day — and telling you in plain language what to do next.',
      simplePoints: [
        'One dashboard with your real sales, across every channel, in real time.',
        'Weekly reports in plain language — not spreadsheets you never open.',
        'Alerts when something is wrong: food cost spikes, sales drops, bad reviews.',
        'Answers, not just charts: "raise this price", "cut this item", "staff less on Tuesdays".',
      ],
      servicesHeading: 'What lives inside this category',
      servicesIntro: 'Four services that turn your data into decisions. Click any of them to go deeper.',
      services: [
        {
          icon: 'PieChart',
          name: 'Data & Analytics',
          what: 'We centralize your sales, labor and channel data into one clean source of truth.',
          plain: 'In plain words: all your numbers, finally in one trustworthy place.',
          href: '/services/data-analytics',
        },
        {
          icon: 'LayoutDashboard',
          name: 'Dashboards',
          what: 'Live dashboards showing exactly how your restaurant is performing right now.',
          plain: 'In plain words: your restaurant’s vital signs, on your phone.',
          href: '/services/dashboards',
        },
        {
          icon: 'FileBarChart',
          name: 'Performance Reporting',
          what: 'Executive weekly and monthly reports with recommendations, not just numbers.',
          plain: 'In plain words: someone reads the data for you and tells you what matters.',
          href: '/services/performance-reporting',
        },
        {
          icon: 'Lightbulb',
          name: 'Business Intelligence',
          what: 'Your General Manager AI — deep analysis that finds opportunities humans miss.',
          plain: 'In plain words: a brain that studies your business 24/7 and never sleeps.',
          href: '/services/business-intelligence',
        },
      ],
      forYouHeading: 'This is for you if…',
      forYou: [
        'You find out about a bad month… when the month is already over.',
        'You "feel" some dishes don’t sell but have no data to prove it.',
        'Every platform gives you a different sales number.',
        'You make pricing decisions based on gut, not numbers.',
      ],
      ctaTitle: 'See what your data has been trying to tell you.',
      ctaText:
        'Our free audit includes a data health check — we’ll show you the numbers you’re not seeing today.',
    },
    es: {
      eyebrow: 'INTELIGENCIA DEL RESTAURANTE',
      title: 'Deja de Adivinar.',
      titleAccent: 'Empieza a Saber.',
      intro:
        'Tu restaurante genera miles de datos cada día — ventas, personal, delivery, reseñas. Nosotros convertimos ese ruido en respuestas claras: qué vender, cuándo reforzar personal, por dónde se fuga el dinero.',
      simpleHeading: '¿Qué es esto, en palabras simples?',
      simpleAnalogy:
        'Es como tener un contador, un analista y un consultor revisando tus números todos los días — y diciéndote en lenguaje claro qué hacer después.',
      simplePoints: [
        'Un dashboard con tus ventas reales, de todos los canales, en tiempo real.',
        'Reportes semanales en lenguaje claro — no hojas de cálculo que nunca abres.',
        'Alertas cuando algo anda mal: costo de comida disparado, caída de ventas, malas reseñas.',
        'Respuestas, no solo gráficas: "sube este precio", "quita este platillo", "menos personal los martes".',
      ],
      servicesHeading: 'Qué incluye esta categoría',
      servicesIntro: 'Cuatro servicios que convierten tus datos en decisiones. Haz clic en cualquiera para profundizar.',
      services: [
        {
          icon: 'PieChart',
          name: 'Datos y Analítica',
          what: 'Centralizamos tus datos de ventas, personal y canales en una sola fuente confiable.',
          plain: 'En palabras simples: todos tus números, por fin en un solo lugar confiable.',
          href: '/services/data-analytics',
        },
        {
          icon: 'LayoutDashboard',
          name: 'Dashboards',
          what: 'Tableros en vivo que muestran exactamente cómo va tu restaurante ahora mismo.',
          plain: 'En palabras simples: los signos vitales de tu restaurante, en tu celular.',
          href: '/services/dashboards',
        },
        {
          icon: 'FileBarChart',
          name: 'Reportes de Rendimiento',
          what: 'Reportes ejecutivos semanales y mensuales con recomendaciones, no solo números.',
          plain: 'En palabras simples: alguien lee los datos por ti y te dice qué importa.',
          href: '/services/performance-reporting',
        },
        {
          icon: 'Lightbulb',
          name: 'Business Intelligence',
          what: 'Tu General Manager AI — análisis profundo que encuentra oportunidades que los humanos no ven.',
          plain: 'En palabras simples: un cerebro que estudia tu negocio 24/7 y nunca duerme.',
          href: '/services/business-intelligence',
        },
      ],
      forYouHeading: 'Esto es para ti si…',
      forYou: [
        'Te enteras de un mal mes… cuando el mes ya terminó.',
        '"Sientes" que algunos platillos no venden pero no tienes datos para probarlo.',
        'Cada plataforma te da un número de ventas diferente.',
        'Decides precios por intuición, no por números.',
      ],
      ctaTitle: 'Descubre lo que tus datos han intentado decirte.',
      ctaText:
        'Nuestra auditoría gratuita incluye un chequeo de salud de tus datos — te mostraremos los números que hoy no estás viendo.',
    },
  },

  'ai-solutions': {
    slug: 'ai-solutions',
    icon: 'Bot',
    en: {
      eyebrow: 'AI SOLUTIONS',
      title: 'AI That Works Shifts,',
      titleAccent: 'Not Just Demos.',
      intro:
        'Forget the hype. Our AI answers your phone, replies to your reviews, gets you found on ChatGPT and Google AI, and automates the repetitive work that eats your managers’ time.',
      simpleHeading: 'What is this, in plain words?',
      simpleAnalogy:
        'Think of it as hiring employees who never sleep, never call in sick and cost a fraction of a salary — each one trained for a specific job in your restaurant.',
      simplePoints: [
        'An AI receptionist that answers every call — even during the Friday rush.',
        'Every review answered professionally within minutes, in any language.',
        'Your restaurant recommended when people ask ChatGPT "where should I eat?"',
        'Repetitive tasks (reports, follow-ups, updates) done automatically.',
      ],
      servicesHeading: 'What lives inside this category',
      servicesIntro: 'Six AI solutions, each with one clear job. Click any of them to go deeper.',
      services: [
        {
          icon: 'Search',
          name: 'Index AI',
          what: 'Visibility engine for Google, Maps and AI search (ChatGPT, Gemini, Perplexity).',
          plain: 'In plain words: being the answer when AI decides where people eat.',
          href: '/index-ai',
        },
        {
          icon: 'Users',
          name: 'AI Agents',
          what: 'A 24/7 AI workforce handling guest communication and operations tasks.',
          plain: 'In plain words: digital employees that never miss a shift.',
          href: '/ai-agents-for-restaurants',
        },
        {
          icon: 'Phone',
          name: 'AI Receptionist',
          what: 'Answers calls, takes reservations and handles common questions automatically.',
          plain: 'In plain words: never miss a call — or the sale attached to it.',
          href: '/ai/ai-receptionist',
        },
        {
          icon: 'Star',
          name: 'Review Automation',
          what: 'Professional, on-brand responses to every review, automatically.',
          plain: 'In plain words: your reputation managed while you run the kitchen.',
          href: '/ai/review-automation',
        },
        {
          icon: 'Workflow',
          name: 'Custom AI Automation',
          what: 'Tailored workflows connecting your systems — built for your exact operation.',
          plain: 'In plain words: we automate the annoying stuff specific to YOUR restaurant.',
          href: '/ai/custom-ai-automation',
        },
        {
          icon: 'Utensils',
          name: 'Catering AI',
          what: 'AI that finds, quotes and follows up on catering opportunities.',
          plain: 'In plain words: a catering sales rep powered by AI. Being finalized.',
          href: '/ai/catering-ai',
          comingSoon: true,
        },
      ],
      forYouHeading: 'This is for you if…',
      forYou: [
        'You miss calls during rush hours — and each call is a lost sale.',
        'Reviews pile up unanswered because nobody has time.',
        'You keep hearing about AI but don’t know where it actually helps.',
        'Your managers spend hours on tasks a robot should do.',
      ],
      ctaTitle: 'See which AI fits your restaurant — free.',
      ctaText:
        'Our free audit shows exactly where AI would save you the most time and money. No tech knowledge needed.',
    },
    es: {
      eyebrow: 'SOLUCIONES DE IA',
      title: 'IA Que Trabaja Turnos,',
      titleAccent: 'No Solo Demos.',
      intro:
        'Olvídate del humo. Nuestra IA contesta tu teléfono, responde tus reseñas, hace que te encuentren en ChatGPT y Google AI, y automatiza el trabajo repetitivo que consume el tiempo de tus gerentes.',
      simpleHeading: '¿Qué es esto, en palabras simples?',
      simpleAnalogy:
        'Piénsalo como contratar empleados que nunca duermen, nunca se enferman y cuestan una fracción de un salario — cada uno entrenado para una tarea específica de tu restaurante.',
      simplePoints: [
        'Una recepcionista IA que contesta todas las llamadas — incluso en la hora pico del viernes.',
        'Cada reseña respondida profesionalmente en minutos, en cualquier idioma.',
        'Tu restaurante recomendado cuando la gente pregunta a ChatGPT "¿dónde como?"',
        'Tareas repetitivas (reportes, seguimientos, actualizaciones) hechas automáticamente.',
      ],
      servicesHeading: 'Qué incluye esta categoría',
      servicesIntro: 'Seis soluciones de IA, cada una con una tarea clara. Haz clic en cualquiera para profundizar.',
      services: [
        {
          icon: 'Search',
          name: 'Index AI',
          what: 'Motor de visibilidad para Google, Maps y búsqueda con IA (ChatGPT, Gemini, Perplexity).',
          plain: 'En palabras simples: ser la respuesta cuando la IA decide dónde come la gente.',
          href: '/index-ai',
        },
        {
          icon: 'Users',
          name: 'Agentes de IA',
          what: 'Un equipo de IA 24/7 gestionando comunicación con clientes y tareas operativas.',
          plain: 'En palabras simples: empleados digitales que nunca faltan a un turno.',
          href: '/ai-agents-for-restaurants',
        },
        {
          icon: 'Phone',
          name: 'Recepcionista IA',
          what: 'Contesta llamadas, toma reservas y resuelve preguntas comunes automáticamente.',
          plain: 'En palabras simples: nunca más pierdas una llamada — ni la venta que traía.',
          href: '/ai/ai-receptionist',
        },
        {
          icon: 'Star',
          name: 'Automatización de Reseñas',
          what: 'Respuestas profesionales y con tu tono de marca a cada reseña, automáticamente.',
          plain: 'En palabras simples: tu reputación gestionada mientras tú manejas la cocina.',
          href: '/ai/review-automation',
        },
        {
          icon: 'Workflow',
          name: 'Automatización IA Personalizada',
          what: 'Flujos a medida que conectan tus sistemas — construidos para tu operación exacta.',
          plain: 'En palabras simples: automatizamos lo tedioso y específico de TU restaurante.',
          href: '/ai/custom-ai-automation',
        },
        {
          icon: 'Utensils',
          name: 'Catering AI',
          what: 'IA que encuentra, cotiza y da seguimiento a oportunidades de catering.',
          plain: 'En palabras simples: un vendedor de catering impulsado por IA. En fase final.',
          href: '/ai/catering-ai',
          comingSoon: true,
        },
      ],
      forYouHeading: 'Esto es para ti si…',
      forYou: [
        'Pierdes llamadas en horas pico — y cada llamada es una venta perdida.',
        'Las reseñas se acumulan sin responder porque nadie tiene tiempo.',
        'Escuchas hablar de IA por todos lados pero no sabes dónde ayuda de verdad.',
        'Tus gerentes pasan horas en tareas que debería hacer un robot.',
      ],
      ctaTitle: 'Descubre qué IA le queda a tu restaurante — gratis.',
      ctaText:
        'Nuestra auditoría gratuita te muestra exactamente dónde la IA te ahorraría más tiempo y dinero. No necesitas saber de tecnología.',
    },
  },
};
